import { useState, useMemo } from "react";
import { useLoanPayrollDeductions, useMarkDeducted, useSendToPayroll } from "@/hooks/useLoans";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/StatusBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader, CheckCircle, Clock, AlertTriangle, Send, DollarSign,
  ChevronLeft, ChevronRight, User, Phone, Banknote,
} from "lucide-react";
import { format } from "date-fns";
import { formatCurrency, cn } from "@/lib/utils";
import { toast } from "sonner";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const statusColors: Record<string, string> = {
  awaiting_leader_approval: "bg-amber-100 text-amber-700 border-amber-200",
  approved_by_leader: "bg-green-100 text-green-700 border-green-200",
  ready_for_hr: "bg-blue-100 text-blue-700 border-blue-200",
  sent_to_hr: "bg-purple-100 text-purple-700 border-purple-200",
  deducted: "bg-green-100 text-green-700 border-green-200",
  failed: "bg-red-100 text-red-700 border-red-200",
  cancelled: "bg-gray-100 text-gray-700 border-gray-200",
};

interface Props {
  selectedMonth: number;
  selectedYear: number;
}

export default function LoanRepaymentsPayroll({ selectedMonth, selectedYear }: Props) {
  const [filterStatus, setFilterStatus] = useState("all");
  const markDeducted = useMarkDeducted();
  const sendToPayroll = useSendToPayroll();

  const { data, isLoading } = useLoanPayrollDeductions(
    selectedMonth,
    selectedYear,
    filterStatus === "all" ? undefined : filterStatus
  );

  const deductions = data?.deductions || [];
  const summary = data?.summary || {};

  const handleMarkDeducted = async (id: number) => {
    try {
      await markDeducted.mutateAsync(id);
      toast.success("Marked as deducted and applied to loan balance");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to mark deducted");
    }
  };

  const handleSendToHr = async (id: number) => {
    try {
      await sendToPayroll.mutateAsync(id);
      toast.success("Sent to HR for payroll processing");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to send to payroll");
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Total</span>
              <DollarSign className="h-4 w-4 text-blue-600" />
            </div>
            <p className="text-2xl font-bold">{summary.total || 0}</p>
            <p className="text-xs text-gray-400">{formatCurrency(summary.totalAmount || 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Pending Approval</span>
              <Clock className="h-4 w-4 text-amber-600" />
            </div>
            <p className="text-2xl font-bold text-amber-700">{summary.awaitingApproval || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Ready for HR</span>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-700">{summary.readyForHr || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Sent to HR</span>
              <Send className="h-4 w-4 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-700">{summary.sentToHr || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Deducted</span>
              <Banknote className="h-4 w-4 text-[#009A44]" />
            </div>
            <p className="text-2xl font-bold text-[#009A44]">{summary.deducted || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500">Filter:</span>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="awaiting_leader_approval">Awaiting Approval</SelectItem>
            <SelectItem value="ready_for_hr">Ready for HR</SelectItem>
            <SelectItem value="sent_to_hr">Sent to HR</SelectItem>
            <SelectItem value="deducted">Deducted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Deductions Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="h-8 w-8 animate-spin text-green-600" />
        </div>
      ) : deductions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No loan deductions found for {MONTHS[selectedMonth - 1]} {selectedYear}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Member</th>
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Phone</th>
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Loan ID</th>
                  <th className="text-right py-3 px-3 font-medium text-gray-600">Monthly Installment</th>
                  <th className="text-right py-3 px-3 font-medium text-gray-600">Amount to Deduct</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">Due Month</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">Approval Status</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">Payroll Status</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {deductions.map((d: any) => (
                  <tr key={d.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-3">
                      <p className="font-medium">{d.member?.name || "Unknown Member"}</p>
                      <p className="text-[10px] text-gray-400">{d.member?.memberId}</p>
                    </td>
                    <td className="py-3 px-3 text-gray-600">{d.member?.phone || "N/A"}</td>
                    <td className="py-3 px-3 font-mono text-xs">{d.loan?.loanCode || "N/A"}</td>
                    <td className="py-3 px-3 text-right">{formatCurrency(d.loan?.monthlyPayment || 0)}</td>
                    <td className="py-3 px-3 text-right font-bold text-[#009A44]">{formatCurrency(d.amount)}</td>
                    <td className="py-3 px-3 text-center text-xs">
                      {MONTHS[d.dueMonth - 1]} {d.dueYear}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <Badge className={cn("text-[9px] border", statusColors[d.status])}>
                        {d.status.replace(/_/g, " ")}
                      </Badge>
                    </td>
                    <td className="py-3 px-3 text-center">
                      {d.repayment?.payrollStatus && (
                        <Badge variant="outline" className="text-[9px]">
                          {d.repayment.payrollStatus.replace(/_/g, " ")}
                        </Badge>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center justify-center gap-1">
                        {d.status === "ready_for_hr" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs text-purple-700 border-purple-300 hover:bg-purple-50"
                            onClick={() => handleSendToHr(d.repayment?.id)}
                            disabled={sendToPayroll.isLoading}
                          >
                            <Send className="h-3 w-3 mr-1" />
                            Send to HR
                          </Button>
                        )}
                        {d.status === "sent_to_hr" && (
                          <Button
                            size="sm"
                            className="h-7 px-2 text-xs bg-[#009A44] hover:bg-[#008238] text-white"
                            onClick={() => handleMarkDeducted(d.repayment?.id)}
                            disabled={markDeducted.isLoading}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Mark Deducted
                          </Button>
                        )}
                        {d.status === "awaiting_leader_approval" && (
                          <Badge variant="outline" className="text-[9px] text-amber-600">
                            <Clock className="h-3 w-3 mr-1" />
                            Awaiting
                          </Badge>
                        )}
                        {d.status === "deducted" && (
                          <Badge className="text-[9px] bg-green-100 text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Done
                          </Badge>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
